<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: meta.links.show,
                    },
                    {
                        name: $tc('messages.questions', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex space-x-2 justify-between">
            <div>
                <aside class="justify-self-center">
                    <nav
                        class="inline-flex items-center justify-center p-1 space-x-1 overflow-hidden text-sm text-gray-600 bg-gray-100 rounded-xl"
                    >
                        <template v-for="link in meta.menu.links" :key="link.url">
                            <template v-if="link.selected">
                                <AppButton :href="link.url" element="inertia-link">
                                    {{ link.display_name }}
                                </AppButton>
                            </template>
                            <template v-else>
                                <InertiaLink
                                    :href="link.url"
                                    class="inline-flex items-center h-8 px-5 font-medium transition rounded-lg hover:text-gray-800 focus:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-inset"
                                >
                                    {{ link.display_name }}
                                </InertiaLink>
                            </template>
                        </template>
                    </nav>
                </aside>
            </div>
            <div>
                <ImportActivityQuestionsPanel :endpoint="importQuestionsEndpoint" :questionCount="questions.length" />
            </div>
        </div>

        <QuestionCreateFormPanel :meta="meta" :defaults="defaults" :can-add-questions="activity.can.add_questions" />

        <div class="md:grid md:grid-cols-2 md:gap-6 mt-5">
            <div
                v-for="question in questions.data"
                class="rounded-lg border-2 border-gray-300 py-2 px-3 shadow-lg"
                :key="`${question.id}_${question.updated_at}`"
            >
                <QuestionBaseDisplay :question="question">
                    <div>
                        <div class="flex justify-between p-1">
                            <div class="inline-block">
                                <QuestionEditFormPanel :question="question" />
                            </div>
                            <div class="inline-flex space-x-1 items-center">
                                <InertiaSquareConfirmActionButton
                                    :href="question.links.detach"
                                    danger
                                    :title="$t('messages.are_you_sure?')"
                                    :confirm-text="$t('messages.remove')"
                                    method="delete"
                                    v-if="question.can.detach"
                                >
                                    <template v-slot:icon>
                                        <svg
                                            viewBox="0 0 20 20"
                                            class="trash w-4 h-4"
                                            stroke-width="2"
                                            stroke="currentColor"
                                            fill="none"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path
                                                d="M19 20h-10.5l-4.21 -4.3a1 1 0 0 1 0 -1.41l10 -10a1 1 0 0 1 1.41 0l5 5a1 1 0 0 1 0 1.41l-9.2 9.3"
                                            ></path>
                                            <path d="M18 13.3l-6.3 -6.3"></path>
                                        </svg>
                                    </template>

                                    <div class="flex flex-col">
                                        <AlertMessage
                                            class="mt-5 inline-block"
                                            v-if="question.answers_in_activity_count"
                                        >
                                            <template v-slot:title>
                                                {{ $t('messages.warning') }}
                                            </template>
                                            <template v-slot:body>
                                                {{ $t('messages.activity_in_programming_deletion_warning') }}
                                            </template>
                                        </AlertMessage>
                                    </div>
                                </InertiaSquareConfirmActionButton>
                                <InertiaSquareConfirmActionButton
                                    :href="question.links.delete"
                                    danger
                                    :title="$t('messages.are_you_sure?')"
                                    :confirm-text="$t('messages.delete')"
                                    method="delete"
                                    v-if="question.can.delete"
                                >
                                    <template v-slot:icon>
                                        <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                            <path
                                                fill-rule="evenodd"
                                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </template>

                                    <div class="flex flex-col">
                                        <AlertMessage class="mt-5 inline-block" v-if="question.activities_count > 1">
                                            <template v-slot:title>
                                                {{ $t('messages.warning') }}
                                            </template>
                                            <template v-slot:body>
                                                {{
                                                    $t('messages.question_attached_to_X_activities_warning', {
                                                        X: question.activities_count - 1,
                                                    })
                                                }}
                                            </template>
                                        </AlertMessage>
                                    </div>
                                </InertiaSquareConfirmActionButton>
                                <QuestionCreateFormPanel
                                    :meta="meta"
                                    :defaults="defaults"
                                    :question="question"
                                    :can-add-questions="activity.can.add_questions"
                                />
                            </div>
                        </div>
                        <div class="font-semibold text-gray-900 truncate">
                            {{ question.title }}
                        </div>
                        <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.description">
                            <QuestionDescriptionDisplay :question="question" />
                        </div>
                        <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.image_path">
                            <img :src="question.image_path" alt="cover" class="h-auto" />
                        </div>
                        <div class="mt-3 p-2 bg-gray-100 rounded-lg" v-if="question.shared_image">
                            <img :src="question.shared_image" alt="cover" class="h-auto" />
                        </div>
                        <div class="mt-3 p-2" v-if="question.graph">
                            <QuestionLineGraph :data="question.graph" v-if="question.graph.type === 'R1Did'" />
                            <GraphToolbox
                                :elements="question.graph.elements"
                                :graph="question.graph.graph"
                                v-if="question.graph.type === 'GraphToolbox'"
                            />
                            <GraphFunctionPlot
                                :elements="question.graph.elements"
                                :graph="question.graph.graph"
                                v-if="question.graph.type === 'GraphFunctionPlot'"
                            />
                        </div>
                        <div>
                            <CopyrightNotice :info="question.copyright" />
                        </div>

                        <HintAlert v-if="question.hint" class="mt-1">
                            <span class="font-semibold">{{ $t('messages.hint') }}:</span>
                            {{ question.hint }}
                        </HintAlert>

                        <component :is="question.type" :question="question" />
                    </div>

                    <div class="mt-1 flex flex-wrap space-x-2">
                        <BadgePill v-for="tag in question.tags" :key="tag">
                            {{ tag }}
                        </BadgePill>
                    </div>
                </QuestionBaseDisplay>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="questions" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import AppButton from '@/components/AppButton.vue';
import QuestionCreateFormPanel from '@/components/Quiz/QuestionCreateFormPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import SmallNumberRangeIndicator from '@/components/SmallNumberRangeIndicator.vue';
import QuestionEditFormPanel from '@/components/Quiz/QuestionEditFormPanel.vue';
import QuestionMultipleChoiceViewOnly from '@/components/Quiz/ViewOnly/QuestionMultipleChoiceViewOnly.vue';
import QuestionShortAnswerViewOnly from '@/components/Quiz/ViewOnly/QuestionShortAnswerViewOnly.vue';
import QuestionTrueFalseViewOnly from '@/components/Quiz/ViewOnly/QuestionTrueFalseViewOnly.vue';
import HintAlert from '@/components/Quiz/HintAlert.vue';
import QuestionOrderPhraseViewOnly from '@/components/Quiz/ViewOnly/QuestionOrderPhraseViewOnly.vue';
import QuestionOrderElementsViewOnly from '@/components/Quiz/ViewOnly/QuestionOrderElementsViewOnly.vue';
import ImportActivityQuestionsPanel from '@/components/pageSpecific/ImportActivityQuestionsPanel.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import QuestionIcon from '@/components/QuestionIcon.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import QuestionDescriptionDisplay from '@/components/Quiz/QuestionDescriptionDisplay.vue';
import QuestionMathFractionViewOnly from '@/components/Quiz/ViewOnly/QuestionMathFractionViewOnly.vue';
import QuestionMathNumberWithToleranceViewOnly from '@/components/Quiz/ViewOnly/QuestionMathNumberWithToleranceViewOnly.vue';
import QuestionMultipleAnswerViewOnly from '@/components/Quiz/ViewOnly/QuestionMultipleAnswerViewOnly.vue';
import QuestionMathXyzAnswersViewOnly from '@/components/Quiz/ViewOnly/QuestionMathXyzAnswersViewOnly.vue';
import QuestionMathMultipleMagnitudesViewOnly from '@/components/Quiz/ViewOnly/QuestionMathMultipleMagnitudesViewOnly.vue';
import QuestionMathPrimeFactorsViewOnly from '@/components/Quiz/ViewOnly/QuestionMathPrimeFactorsViewOnly.vue';
import QuestionBaseDisplay from '@/components/Quiz/QuestionBaseDisplay.vue';
import QuestionMathPolynomialOperationViewOnly from '@/components/Quiz/ViewOnly/QuestionMathPolynomialOperationViewOnly.vue';
import CopyrightNotice from '@/components/CopyrightNotice.vue';
import QuestionLineGraph from '@/components/Quiz/QuestionLineGraph.vue';
import GraphToolbox from '../../../../components/Quiz/GraphToolbox.vue';
import GraphFunctionPlot from '../../../../components/Quiz/GraphFunctionPlot.vue';
import QuestionMathLatexAnswerViewOnly from '@/components/Quiz/ViewOnly/QuestionMathLatexAnswerViewOnly.vue';
// { question_view_only_import }

export default {
    components: {
        GraphFunctionPlot,
        GraphToolbox,
        QuestionLineGraph,
        CopyrightNotice,
        QuestionBaseDisplay,
        QuestionDescriptionDisplay,
        InertiaPagination,
        QuestionIcon,
        FormTextarea,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        AppButton,
        QuestionCreateFormPanel,
        QuestionEditFormPanel,
        BadgePill,
        SmallNumberRangeIndicator,
        HintAlert,
        ImportActivityQuestionsPanel,
        InertiaSquareConfirmActionButton,
        AlertMessage,
        'multiple-choice': QuestionMultipleChoiceViewOnly,
        'short-answer': QuestionShortAnswerViewOnly,
        'math-fraction': QuestionMathFractionViewOnly,
        'math-number-with-tolerance': QuestionMathNumberWithToleranceViewOnly,
        'order-phrase': QuestionOrderPhraseViewOnly,
        'order-elements': QuestionOrderElementsViewOnly,
        'true-false': QuestionTrueFalseViewOnly,
        'multiple-answer': QuestionMultipleAnswerViewOnly,
        'math-xyz-answers': QuestionMathXyzAnswersViewOnly,
        'math-multiple-magnitudes': QuestionMathMultipleMagnitudesViewOnly,
        'math-prime-factors': QuestionMathPrimeFactorsViewOnly,
        'math-polynomial-operation': QuestionMathPolynomialOperationViewOnly,
        'math-latex-answer': QuestionMathLatexAnswerViewOnly,
        // { question_view_only_component }
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'questions', 'activity', 'defaults', 'importQuestionsEndpoint'],
    data() {
        return {
            another: false,
        };
    },
};
</script>
